import { Flex, Text } from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { Button as DSButton } from 'src/design_system/components/Button/Button'

type PlanDetail = {
  detail: string
}

type FullImpactPackageTileProps = {
  icon: React.ReactNode
  title: string
  price?: string
  details: PlanDetail[]
  onAddToCart: () => void
  gradient: string
  isMobile: boolean
  isTablet: boolean
}

export const FullImpactPackageTile: React.FC<FullImpactPackageTileProps> = ({
  icon,
  title,
  price,
  details,
  onAddToCart,
  gradient,
  isMobile,
  isTablet,
}) => {
  const optionalFreeLabel = 'Optional First Year Consultant FREE'
  const formattedPrice = price?.trim() ?? ''
  const priceMatch = formattedPrice.match(/^\$?(\d+)(?:\.(\d+))?$/)
  const priceParts = priceMatch
    ? {
        currency: formattedPrice.startsWith('$') ? '$' : '',
        whole: priceMatch[1],
        cents: priceMatch[2] ?? '00',
      }
    : null
  const priceLabel =
    priceParts && priceParts.cents === '00'
      ? `${priceParts.currency}${priceParts.whole}`
      : priceParts
        ? `${priceParts.currency}${priceParts.whole}.${priceParts.cents}`
        : formattedPrice

  return (
    <Flex width="100%" justifyContent="center">
      <Flex
        width={isTablet ? '90%' : '960px'}
        maxWidth="840px"
        borderRadius="large"
        overflow="hidden"
        style={{
          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
        }}
        flexDirection={isTablet ? 'column' : 'row'}
      >
        <Flex
          flexDirection="column"
          width={isTablet ? '100%' : '33.333%'}
          flexGrow={isTablet ? 1 : 0}
          alignItems="flex-start"
          backgroundColor="#FFFFFF"
          inset={{ horizontal: 'x4', vertical: 'x5' }}
          rowGap="x1_5"
          style={{
            minWidth: isTablet ? 'auto' : '380px',
            maxWidth: isTablet ? '100%' : '33.333%',
          }}
        >
          {isMobile ? (
            <Flex alignItems="center" flexDirection="row">
              {icon}
              <Text
                type="subtitle"
                color="primaryHeading"
                content={title}
                style={{
                  fontSize: 40,
                  lineHeight: 1.25,
                  fontWeight: 400,
                  color: '#6B7280',
                  marginLeft: 8,
                }}
              />
            </Flex>
          ) : (
            <>
              <Flex>{icon}</Flex>
              <Text
                type="subtitle"
                color="primaryHeading"
                content={title}
                style={{
                  fontSize: 40,
                  lineHeight: 1.3,
                  fontWeight: 400,
                  color: '#6B7280',
                }}
              />
            </>
          )}
          <Flex flexGrow={1} />
          <Flex
            width="100%"
            inset={{ top: 'x2' }}
            justifyContent="center"
            style={{ marginBottom: 8 }}
          >
            <Flex width="100%" maxWidth="240px" justifyContent="center">
              <DSButton
                background={gradient}
                textColor="#FFFFFF"
                textWeight={600}
                textSize="0.875rem"
                padding="0.75rem 1rem"
                borderRadius="12px"
                width="100%"
                onClick={onAddToCart}
              >
                <Flex
                  width="100%"
                  alignItems="center"
                  justifyContent="center"
                  style={{
                    minHeight: 18,
                  }}
                >
                  <Text
                    type="body"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: '#FFFFFF',
                      paddingRight: 12,
                    }}
                  >
                    Add to Cart
                  </Text>
                  {priceLabel ? (
                    <>
                      <div
                        aria-hidden="true"
                        style={{
                          width: 1,
                          height: 18,
                          background: 'rgba(255,255,255,0.55)',
                        }}
                      />
                      <Text
                        type="body"
                        style={{
                          fontSize: 20,
                          fontWeight: 600,
                          color: '#FFFFFF',
                          paddingLeft: 12,
                        }}
                      >
                        {priceLabel}
                      </Text>
                    </>
                  ) : null}
                </Flex>
              </DSButton>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          flexDirection="column"
          justifyContent="center"
          width={isTablet ? '100%' : '66.667%'}
          inset={{
            horizontal: isMobile ? 'x3' : 'x5',
            vertical: isMobile ? 'x3' : 'x5',
          }}
          rowGap={isMobile ? 'x1_5' : 'x2'}
          style={{
            background: gradient,
            color: '#FFFFFF',
            maxWidth: isTablet ? '100%' : '66.667%',
          }}
        >
          {details.map((detail, index) => {
            const isOptionalFree =
              detail.detail.trim() === optionalFreeLabel.trim()
            return (
              <Flex key={`full-impact-feature-${index}`} alignItems="center">
                {isOptionalFree ? (
                  <Icon
                    name="asterisk"
                    color="#FFFFFF"
                    size={isMobile ? 16 : 20}
                    strokeWidth={2}
                  />
                ) : (
                  <Icon
                    name="v_check"
                    color="#FFFFFF"
                    size={isMobile ? 16 : 20}
                  />
                )}
                <Text
                  type={isMobile ? 'body' : 'largeBody'}
                  color="accent"
                  style={{
                    color: '#FFFFFF',
                    fontSize: isMobile ? 13 : undefined,
                    fontWeight: isOptionalFree ? 700 : undefined,
                  }}
                  outset={{ left: 'x1' }}
                >
                  {detail.detail}
                </Text>
              </Flex>
            )
          })}
        </Flex>
      </Flex>
    </Flex>
  )
}
